import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { Box, Typography, TextField, Button, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import AuthLayout from './AuthLayout';
import TabSwitcher from './TabSwitcher';

const AuthContainer = ({ mode = 'login' }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { login, register } = useAuth();
  const [activeTab, setActiveTab] = useState(mode === 'register' ? 1 : 0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasFailedLogin, setHasFailedLogin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const isRegister = activeTab === 1;

  const tabs = [
    { label: 'Sign In' },
    { label: 'Create account' },
  ];

  const handleTabChange = (tabIndex) => {
    setActiveTab(tabIndex);
    setError('');
    setHasFailedLogin(false);
    if (tabIndex === 0) {
      navigate('/login');
    } else {
      navigate('/register');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setError('');

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    if (isRegister) {
      // Register - Validate
      if (!formData.name.trim()) {
        setError('Full name is required');
        return;
      }

      if (formData.password.length < 8) {
        setError('Password must be at least 8 characters');
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return;
      }

      setLoading(true);

      console.log('🟡 [AUTH] Clearing any existing auth state before registration');
      localStorage.removeItem('token');

      const nameParts = formData.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const userData = {
        firstName,
        lastName,
        email: formData.email,
        password: formData.password,
      };

      try {
        const result = await register(userData);
        console.log('🟢 [AUTH] Registration result:', JSON.stringify(result, null, 2));

        // Registration proves nothing yet — the address has to be verified
        // before an authenticator is enrolled or any session exists.
        if (result && result.requiresVerification) {
          navigate('/email-code', {
            replace: true,
            state: { email: result.email || formData.email, purpose: 'register' }
          });
          return;
        }

        if (result && result.success) {
          navigate('/profile');
        } else {
          setError(result?.message || 'Registration failed');
        }
      } catch (error) {
        setError(error.message || 'Registration failed. Please try again.');
      } finally {
        setLoading(false);
      }
    } else {
      // Login
      setLoading(true);

      try {
        const result = await login(formData.email, formData.password);
        console.log('🟢 [AUTH] Login result:', JSON.stringify(result, null, 2));

        // Suppliers: the server has already emailed a sign-in code. The
        // password travels in navigation state only so the "send a new code"
        // link on the next screen can re-authenticate.
        if (result && result.requiresEmailOtp) {
          setHasFailedLogin(false);
          setError('');
          navigate('/email-code', {
            replace: true,
            state: {
              email: result.email || formData.email,
              purpose: 'login',
              password: formData.password,
            }
          });
          setLoading(false);
          return;
        }

        // Staff only, from here down.
        if (result && result.requiresTOTPSetup) {
          setHasFailedLogin(false);
          setError('');
          navigate('/totp-setup', {
            replace: true,
            state: {
              email: result.email,
              qrCode: result.qrCode,
              secret: result.secret,
            }
          });
          setLoading(false);
          return;
        }

        if (result && result.requiresTOTP) {
          setHasFailedLogin(false);
          setError('');
          navigate('/totp-verify', {
            replace: true,
            state: { email: result.email }
          });
          setLoading(false);
          return;
        }

        if (result && result.requiresVerification) {
          setHasFailedLogin(false);
          setError('');
          navigate('/email-code', {
            replace: true,
            state: { email: formData.email || result.email, purpose: 'register' }
          });
          setLoading(false);
          return;
        }

        if (result && result.success) {
          setHasFailedLogin(false);
          navigate('/dashboard');
        } else {
          setError(result?.message || 'Login failed');
          setHasFailedLogin(true);
          setLoading(false);
        }
      } catch (error) {
        console.error('Login error:', error);
        setError(error.message || 'An error occurred during login. Please try again.');
        setHasFailedLogin(true);
        setLoading(false);
      }
    }
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#fff',
      '& fieldset': { borderColor: '#d1d5db', borderRadius: '8px' },
      '&:hover fieldset': { borderColor: '#9ca3af' },
      '&.Mui-focused fieldset': { borderColor: '#1976d2', borderWidth: '1px' },
    },
    '& .MuiOutlinedInput-input': { padding: '12px 14px', fontSize: '15px' },
  };

  return (
    <AuthLayout>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 'bold',
          mb: 1,
          color: '#000',
          fontSize: { xs: '24px', sm: '28px', md: '34px' },
          textAlign: { xs: 'center', sm: 'left' }
        }}
      >
        {isRegister ? 'Supplier Registration' : 'Welcome back'}
      </Typography>
      <Typography
        sx={{
          color: '#666',
          mb: 4,
          fontSize: { xs: '14px', sm: '16px' },
          textAlign: { xs: 'center', sm: 'left' }
        }}
      >
        {isRegister
          ? 'Register your company as a supplier to get started with onboarding.'
          : 'Sign in to your account to continue.'}
      </Typography>

      <TabSwitcher activeTab={activeTab} onTabChange={handleTabChange} tabs={tabs} />

      {error && (
        <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#ffebee', borderRadius: 1, color: '#c62828', fontSize: '14px' }}>
          {error}
        </Box>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        {isRegister && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ mb: 0.75, fontSize: '15px', fontWeight: 600, color: '#000' }}>
              Name
            </Typography>
            <TextField
              required
              fullWidth
              name="name"
              placeholder="Your name"
              value={formData.name}
              onChange={handleChange}
              sx={fieldSx}
            />
          </Box>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography sx={{ mb: 0.75, fontSize: '15px', fontWeight: 600, color: '#000' }}>
            Email
          </Typography>
          <TextField
            required
            fullWidth
            type="email"
            name="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleChange}
            error={!!formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())}
            helperText={
              formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())
                ? 'Enter a valid email address (e.g. name@example.com)'
                : ''
            }
            sx={fieldSx}
          />
        </Box>

        <Box sx={{ mb: isRegister ? 2 : 3 }}>
          <Typography sx={{ mb: 0.75, fontSize: '15px', fontWeight: 600, color: '#000' }}>
            Password
          </Typography>
          <TextField
            required
            fullWidth
            type={showPassword ? 'text' : 'password'}
            name="password"
            placeholder="Password"
            value={formData.password}
            onChange={handleChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(v => !v)}
                    edge="end"
                    size="small"
                    sx={{ color: '#9ca3af', mr: 0.5 }}
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={fieldSx}
          />
        </Box>

        {/* The password reset flow already existed end to end but nothing
            linked to it, so it was reachable only by typing the URL. */}
        {!isRegister && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -1.5, mb: 2.5 }}>
            <Typography
              component="button"
              type="button"
              onClick={() => navigate('/forgot-password')}
              sx={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                color: theme.palette.green.main,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
                '&:focus-visible': { outline: `2px solid ${theme.palette.green.main}`, outlineOffset: '2px' },
              }}
            >
              Forgot password?
            </Typography>
          </Box>
        )}

        {isRegister && (
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ mb: 0.75, fontSize: '15px', fontWeight: 600, color: '#000' }}>
              Confirm Password
            </Typography>
            <TextField
              required
              fullWidth
              type={showConfirmPassword ? 'text' : 'password'}
              name="confirmPassword"
              placeholder="Confirm Password"
              value={formData.confirmPassword}
              onChange={handleChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(v => !v)}
                      edge="end"
                      size="small"
                      sx={{ color: '#9ca3af', mr: 0.5 }}
                    >
                      {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={fieldSx}
            />
          </Box>
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{
            backgroundColor: theme.palette.green.main,
            color: '#fff',
            textTransform: 'none',
            fontSize: '15px',
            fontWeight: 600,
            height: '48px',
            borderRadius: '8px',
            boxShadow: 'none',
            mb: hasFailedLogin && !isRegister ? 1.5 : 2.5,
            '&:hover': { backgroundColor: theme.palette.green.hover, boxShadow: 'none' },
            '&:disabled': {
              backgroundColor: theme.palette.neutral.gray[300],
              color: theme.palette.text.disabled,
            },
          }}
        >
          {loading
            ? isRegister ? 'Creating account...' : 'Signing in...'
            : isRegister ? 'Create account' : 'SIGN IN'}
        </Button>


        {/* <Box sx={{ display: 'flex', alignItems: 'center', my: 2.5 }}>
          <Box sx={{ flex: 1, height: '1px', backgroundColor: '#e0e0e0' }} />
          <Typography sx={{ px: 2, color: '#999', fontSize: '14px' }}>OR</Typography>
          <Box sx={{ flex: 1, height: '1px', backgroundColor: '#e0e0e0' }} />
        </Box>

        <Button
          fullWidth
          variant="outlined"
          sx={{
            backgroundColor: '#fff',
            color: '#666',
            textTransform: 'none',
            fontSize: '15px',
            fontWeight: 500,
            height: '48px',
            borderRadius: '8px',
            border: '1px solid #d1d5db',
            boxShadow: 'none',
            '&:hover': { backgroundColor: '#f9fafb', border: '1px solid #d1d5db', boxShadow: 'none' },
          }}
        >
          <Box component="img" src="https://www.google.com/favicon.ico" alt="Google" sx={{ width: 18, height: 18, mr: 1.5 }} />
          Sign in with Google
        </Button> */}
      </Box>
    </AuthLayout>
  );
};

export default AuthContainer;
